import React, { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { setDraftStatus } from './draftStatus';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { toast } from 'sonner';
import { resolveOfficialAudio } from '@discovr/features';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useWindowEvent } from '../lib/browser/useEventListener';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Rocket,
  Check,
  FileText,
  NotePencil,
  ClipboardText,
  TextAlignLeft,
  CaretDown,
  X,
  ShoppingBag,
  Trash,
  Plus,
} from '../design-system/icons';
import type { Campaign, CreateCampaignRequest } from '@discovr/types';
import { DELIVERABLE_REGISTRY, isOfferedOnPlatform, hasReelDeliverable, platformLabel } from '@discovr/types';
import {
  campaignSchema, type CampaignFormData, type ProductRow, defaultValues, stepFields,
  FIELD_TO_STEP, computeStepComplete, buildCampaignPayload,
} from './campaignSchema';
import { CONNECT_STEP, TIERS } from './constants';
import {
  visibleSteps, stepPosition, lastStepId, nextStepId, prevStepId, resolveVisibleStep,
} from './steps';
import type { AudienceDropdownKey } from './types';
import type { NewCampaignSearch } from './search';
import {
  readStoredDraft,
  writeStoredDraft,
  clearNewCampaignDraft,
  goalLabel,
  toDateInputValue,
  normalizeMultiValue,
  firstFieldErrorMessage,
  fieldErrorMessage,
  canAddProduct,
  MAX_PRODUCTS,
  DRAFT_SAVE_DEBOUNCE_MS,
} from './helpers';
import { StepBasics } from './StepBasics';
import { StepTargeting } from './StepTargeting';
import { StepBudget } from './StepBudget';

/* ── Step 4 — Campaign Brief (inline, small enough to stay here) ── */
import { lbl, hint, err, invalidBorder } from './constants';

// ── Pure helpers (no hooks — safe to define at module level) ──────────────────

type UsageRights = CampaignFormData['usageRights'];

// Editable list of brief rules, one line per rule in a single textarea. Local
// `text` is the source of truth so a freshly-typed blank line isn't stripped
// under the user; only non-empty lines are pushed to the form. Remounted via a
// `key` in the parent on form reset so stale text never survives a restore.
function BriefList({
  kind,
  items,
  onChange,
}: {
  kind: 'do' | 'dont';
  items: string[];
  onChange: (next: string[]) => void;
}) {
  const isDont = kind === 'dont';
  const [text, setText] = useState(() => items.join('\n'));
  const handleChange = (val: string) => {
    setText(val);
    onChange(val.split('\n').filter((line) => line.trim().length > 0));
  };
  return (
    <div className="flex flex-col gap-2">
      <span className={`flex items-center gap-1.5 text-caption-medium tracking-wide uppercase ${isDont ? 'text-(--foreground-destructive-default)' : 'text-(--foreground-success-default)'}`}>
        {isDont ? <X size={12} weight="bold" /> : <Check size={12} weight="bold" />}
        {isDont ? "Don'ts" : "Do's"}
      </span>
      <Textarea
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        rows={6}
        placeholder={
          isDont
            ? 'e.g. No guaranteed-returns language\nOne rule per line'
            : 'e.g. Open with a personal story\nOne rule per line'
        }
        className={`min-h-[140px] resize-y leading-relaxed ${isDont ? 'border-(--border-destructive-default)/30' : ''}`}
      />
    </div>
  );
}

// Icon + title + subtitle heading for Step 4's cards. Module-level so it keeps
// a stable identity across NewCampaign's re-renders (a new identity per render
// would remount its subtree, disruptive inside the focusable Products toggle).
function SectionHead({
  icon: Icon,
  title,
  sub,
}: {
  icon: typeof NotePencil;
  title: string;
  sub: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center gap-2 text-body-sm-medium text-foreground">
        <Icon size={14} className="text-foreground" /> {title}
      </span>
      <span className="text-caption-regular text-muted-foreground">{sub}</span>
    </div>
  );
}

// Repeatable product rows for the Multi-Product Campaigns catalog. Module-level
// so row <Input>s keep focus across the parent's re-render. Fully-blank rows are
// dropped by the schema's preprocess, so an empty row never blocks the wizard.
function ProductsEditor({
  items,
  onChange,
}: {
  items: ProductRow[];
  onChange: (next: ProductRow[]) => void;
}) {
  const update = (i: number, patch: Partial<ProductRow>) =>
    onChange(items.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  // Gate adding: only when the latest row has a name and we're under the cap.
  const canAdd = canAddProduct(items);
  const add = () => {
    if (!canAdd) return;
    onChange([...items, { name: '', link: '', note: '' }]);
  };
  const atCap = items.length >= MAX_PRODUCTS;
  return (
    <div className="flex flex-col gap-3">
      {items.length > 0 && (
        <div className="hidden gap-3 px-1 sm:grid sm:grid-cols-[1.2fr_1.2fr_1.6fr_auto]">
          <span className="text-caption-medium uppercase tracking-wide text-muted-foreground">
            Product name <span className="text-(--foreground-destructive-default)">*</span>
          </span>
          <span className="text-caption-medium uppercase tracking-wide text-muted-foreground">Product link</span>
          <span className="text-caption-medium uppercase tracking-wide text-muted-foreground">Note for creators</span>
          <span aria-hidden />
        </div>
      )}
      {items.map((row, i) => (
        <div
          key={i}
          className="grid grid-cols-1 items-start gap-2 sm:grid-cols-[1.2fr_1.2fr_1.6fr_auto] sm:gap-3"
        >
          <Input
            value={row.name}
            onChange={(e) => update(i, { name: e.target.value })}
            placeholder="Product name"
            aria-label={`Product ${i + 1} name`}
          />
          <Input
            value={row.link ?? ''}
            onChange={(e) => update(i, { link: e.target.value })}
            placeholder="e.g. https://…"
            aria-label={`Product ${i + 1} link`}
          />
          <Input
            value={row.note ?? ''}
            onChange={(e) => update(i, { note: e.target.value })}
            placeholder="Optional focus instruction"
            aria-label={`Product ${i + 1} note`}
          />
          <button
            type="button"
            onClick={() => remove(i)}
            aria-label={`Remove product ${i + 1}`}
            className="flex size-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-(--foreground-destructive-default)"
          >
            <Trash size={16} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        disabled={!canAdd}
        className={`flex items-center justify-center gap-1.5 rounded-lg border border-dashed py-2.5 text-body-sm-medium transition-colors ${
          canAdd
            ? 'border-border text-primary hover:bg-muted'
            : 'cursor-not-allowed border-border/60 text-muted-foreground'
        }`}
      >
        <Plus size={14} weight="bold" /> Add product
      </button>
      {!canAdd && (
        <p className="text-caption-regular text-muted-foreground">
          {atCap
            ? `You can add up to ${MAX_PRODUCTS} products.`
            : 'Enter a product name to add another product.'}
        </p>
      )}
    </div>
  );
}

function renderDeliverablesSummary(
  deliverables: string[],
  usageRights: UsageRights,
): React.ReactNode {
  const types = deliverables ?? [];
  if (types.length === 0 && !usageRights) {
    return <p className="m-0 text-caption-regular text-muted-foreground">No deliverables selected</p>;
  }
  const mainItems = types.filter((t) => DELIVERABLE_REGISTRY[t]?.category === 'main');
  const addonItems = types.filter((t) => DELIVERABLE_REGISTRY[t]?.category === 'addon');
  return (
    <div className="flex flex-wrap gap-1.5">
      {mainItems.map((t) => (
        <span key={t} className="rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
          {DELIVERABLE_REGISTRY[t]?.label ?? t}
        </span>
      ))}
      {addonItems.map((t) => (
        <span key={t} className="rounded-full border border-border bg-muted px-2.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
          {DELIVERABLE_REGISTRY[t]?.label ?? t}
        </span>
      ))}
      {usageRights?.days && (
        <span className="rounded-full border border-border bg-muted px-2.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
          {usageRights.days}-day rights
        </span>
      )}
    </div>
  );
}

// Campaign-audio chip for the Review step's Deliverables card (Figma 3906-105544):
// a small label + rounded muted chip showing the resolved track name. Instagram-
// only. Prefers the song name already captured at Verify (officialAudioTrack) and
// only re-resolves the URL as a fallback (e.g. a restored draft where the track
// wasn't captured) — avoids a redundant call to the IG-backed resolve endpoint.
function ReviewCampaignAudioChip({ url, songName }: { url: string; songName?: string }) {
  const captured = songName?.trim();
  // Async fallback only — used when the track wasn't captured at Verify (e.g. a
  // restored draft). setState happens only in the async callbacks (never
  // synchronously in the effect body) so it can't cascade renders.
  const [resolved, setResolved] = useState<string | null>(null);
  useEffect(() => {
    // Already have the name from Verify — skip the redundant IG-backed resolve.
    if (captured) return;
    let alive = true;
    const u = url.trim();
    if (!u) return;
    resolveOfficialAudio(u)
      .then((r) => {
        // Main track name only — keep the chip short.
        if (alive) setResolved(r.track_name?.trim() || 'Instagram audio');
      })
      .catch(() => {
        if (alive) setResolved('Instagram audio');
      });
    return () => {
      alive = false;
    };
  }, [url, captured]);

  if (!url.trim()) return null;
  const label = captured || resolved;
  return (
    <div className="mt-3 flex flex-col gap-[7px]">
      <p className="m-0 text-[12px] font-medium text-muted-foreground">Campaign audio</p>
      <span className="inline-flex h-[30px] max-w-[220px] items-center truncate rounded-full border border-border bg-muted px-3 text-[13px] font-medium text-foreground">
        {label ?? 'Resolving…'}
      </span>
    </div>
  );
}

/** The success screen's wording, which depends on who created the campaign. */
export interface WizardSuccessCopy {
  title: string;
  message: string;
  /** Label of the action that opens the created campaign. */
  openLabel: string;
}

// A brand's own campaign goes to the Discovr team for review.
const BRAND_SUCCESS_COPY: WizardSuccessCopy = {
  title: 'Campaign submitted.',
  message: 'Campaign created. Our team will review it within 24-48 hours, and you can start setting it up now.',
  openLabel: 'Go to dashboard',
};

/** What a host's `afterCreate` follow-up reports: shown on the success screen. */
export interface WizardAfterCreate {
  /** One status line about the follow-up (announced to screen readers). */
  note: string;
  /** An extra success-screen action, e.g. to open where the follow-up can be retried. */
  action?: { label: string; onClick: () => void };
}

export interface NewCampaignWizardProps {
  /** Persists the campaign; its rejection message is shown to the user. */
  createCampaign: (payload: CreateCampaignRequest) => Promise<Campaign>;
  /** The success screen's open action, with the created campaign. */
  onCreated: (campaign: Campaign) => void;
  /** The footer's Cancel action. */
  onCancel: () => void;
  /** Rendered as the Connect step; when omitted that step is skipped entirely. */
  connectStep?: ReactNode;
  /** Local-storage key for the in-progress draft — unique per host/brand. Read once on mount: changing it
   *  requires a remount (give the wizard a `key` that changes with it). */
  draftKey: string;
  /** Host follow-up run after the campaign is created and before the draft is cleared (e.g. adding creators the
   *  host handed over). Its note shows on the success screen; a rejection never fails the created campaign. */
  afterCreate?: (campaign: Campaign, platform: string) => Promise<WizardAfterCreate | null>;
  /** Success screen wording; defaults to the brand's submitted-for-review copy. */
  successCopy?: WizardSuccessCopy;
}

export function NewCampaignWizard({
  createCampaign,
  onCreated,
  onCancel,
  connectStep,
  draftKey,
  afterCreate,
  successCopy = BRAND_SUCCESS_COPY,
}: NewCampaignWizardProps) {
  const navigate = useNavigate();
  const steps = visibleSteps(connectStep !== undefined);
  const search = useSearch({ strict: false }) as Partial<NewCampaignSearch>;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // When set, the wizard shows the Figma "Campaign submitted" success screen.
  const [submitted, setSubmitted] = useState<Campaign | null>(null);
  // What the host's afterCreate follow-up reported, for the success screen.
  const [afterNote, setAfterNote] = useState<WizardAfterCreate | null>(null);
  const [customCategoryInput, setCustomCategoryInput] = useState('');
  const [sampleVideoFiles, setSampleVideoFiles] = useState<File[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  // Furthest step reached this session; the stepper lets the user jump freely
  // among reached steps. Moving past it stays gated behind Continue's validation.
  const [maxReachedStep, setMaxReachedStep] = useState(1);
  // Step 4 "Other Input field" collapsible (Figma expanded/collapsed states).
  const [briefExpanded, setBriefExpanded] = useState(false);
  // Products (Multi-Product Campaigns) collapsible — mirrors the brief's
  // "Other input field" expand/collapse.
  const [productsExpanded, setProductsExpanded] = useState(true);
  // Bumped whenever the form is programmatically reset (draft restore,
  // "Create another") — forces BriefList to remount and re-derive its local
  // textarea text from the freshly-reset `dos`/`donts` arrays.
  const [formResetKey, setFormResetKey] = useState(0);
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDraftRef = useRef(false);
  const initializedRef = useRef(false);
  // Scrollable step-content container — Continue advances the step in place
  // (no route change), so without this the wizard stays scrolled wherever
  // the user left it on the previous, often-longer step.
  const stepContentRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    trigger,
    getFieldState,
    getValues,
    reset,
  } = useForm<CampaignFormData>({
    resolver: zodResolver(campaignSchema) as Resolver<CampaignFormData>,
    defaultValues,
  });

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setDraftStatus('idle');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = toDateInputValue(today);
    const stored = readStoredDraft(draftKey);
    // `?step=` (e.g. a shared/bookmarked link) wins over the persisted
    // draft's last-open step.
    const initialStep = resolveVisibleStep(steps, search.step ?? stored?.currentStep ?? 1);
    if (stored) {
      reset({ ...defaultValues, ...stored.formData, startDate: todayStr });
    } else {
      reset({ ...defaultValues, startDate: todayStr });
    }
    setCurrentStep(initialStep);
    // Restore the furthest-reached step (persisted), so a back-nav + reload
    // still lets the user jump forward to where they'd been.
    setMaxReachedStep(Math.max(initialStep, stored?.maxReachedStep ?? initialStep));
    if (stored) {
      persistedStepsRef.current = {
        currentStep: stored.currentStep,
        maxReachedStep: stored.maxReachedStep ?? stored.currentStep,
      };
    }
    setFormResetKey((k) => k + 1);
    // Runs once on mount: draftKey and the step list are fixed for a mounted wizard (hosts remount to change them).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  // Mirror the wizard step into the URL (?step=N) so it's shareable and
  // survives a refresh. `replace` — the wizard's own Back/Continue buttons
  // are the step-navigation UI, so each step shouldn't stack browser history.
  useEffect(() => {
    if (!initializedRef.current) return;
    void navigate({
      to: '.',
      replace: true,
      search: ((p: Partial<NewCampaignSearch>) => ({ ...p, step: currentStep })) as never,
    });
  }, [currentStep, navigate]);

  const draftDirtyRef = useRef(false);
  const stepsRef = useRef({ currentStep, maxReachedStep });
  useEffect(() => {
    stepsRef.current = { currentStep, maxReachedStep };
  }, [currentStep, maxReachedStep]);

  const persistedStepsRef = useRef({ currentStep, maxReachedStep });

  // Writes the latest form + step now; no-op when nothing is pending.
  const flushDraft = useCallback(() => {
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = null;
    if (!draftDirtyRef.current) return;
    draftDirtyRef.current = false;
    persistedStepsRef.current = { ...stepsRef.current };
    writeStoredDraft(draftKey, getValues(), stepsRef.current.currentStep, stepsRef.current.maxReachedStep);
  }, [getValues, draftKey]);

  useEffect(() => {
    const { unsubscribe } = watch(() => {
      draftDirtyRef.current = true;
      setDraftStatus('saving');
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(flushDraft, DRAFT_SAVE_DEBOUNCE_MS);
    });
    return unsubscribe;
  }, [watch, flushDraft]);

  // Any step change (Continue, Back, stepper, invalid-submit jump) must persist the new step,
  // even when the debounce already fired, and must not drop a still-pending edit. Marking here,
  // after commit, also covers a debounce firing between setState and the commit.
  const seenStepsRef = useRef({ currentStep, maxReachedStep });
  useEffect(() => {
    const seen = seenStepsRef.current;
    if (seen.currentStep === currentStep && seen.maxReachedStep === maxReachedStep) return;
    seenStepsRef.current = { currentStep, maxReachedStep };
    const p = persistedStepsRef.current;
    if (p.currentStep !== currentStep || p.maxReachedStep !== maxReachedStep) {
      draftDirtyRef.current = true;
    }
    flushDraft();
  }, [currentStep, maxReachedStep, flushDraft]);

  // Leaving the page (tab close, reload) or the wizard (unmount) writes any pending edit.
  useWindowEvent('pagehide', flushDraft);
  useWindowEvent('beforeunload', flushDraft);
  useEffect(() => flushDraft, [flushDraft]);

  const name = watch('name') ?? '';
  const brief = watch('brief') ?? '';
  const platform = watch('platform');
  const creatorCategories = watch('creatorCategories') ?? [];
  const totalCreators = watch('totalCreators');
  const ageMin = watch('ageMin');
  const ageMax = watch('ageMax');
  const tierPresets = watch('tierPresets') ?? [];
  const primaryGoalVal = watch('primaryGoal');
  const primaryGoalCustomVal = watch('primaryGoalCustom') ?? '';
  const deliverables = watch('deliverables') ?? [];
  const deliverables_text = watch('deliverables_text') ?? '';
  const usageRights = watch('usageRights');
  const startDate = watch('startDate') ?? '';
  const payoutModel = watch('payoutModel');
  const totalBudget = watch('totalBudget');
  const videoTitle = watch('videoTitle') ?? '';
  const primaryFocus = watch('primaryFocus') ?? '';
  const secondaryFocus = watch('secondaryFocus') ?? '';
  const cta = watch('cta') ?? '';
  // Coerce defensively: a pre-migration draft may still hold a string here.
  const dosRaw = watch('dos');
  const dos = Array.isArray(dosRaw) ? dosRaw : [];
  const dontsRaw = watch('donts');
  const donts = Array.isArray(dontsRaw) ? dontsRaw : [];
  const sampleVideoUrl = watch('sampleVideoUrl') ?? '';
  const videoDescription = watch('videoDescription') ?? '';
  const gender = normalizeMultiValue(watch('gender'));
  const language = normalizeMultiValue(watch('language'));
  const region = normalizeMultiValue(watch('region'));
  const creatorGenders = normalizeMultiValue(watch('creatorGenders'));

  // Campaign audio (Official Audio Compliance) only applies to Instagram
  // campaigns with a Reel deliverable selected — CampaignAudioSection is
  // hidden by DeliverablesSection outside that condition, but hiding a
  // controlled field doesn't clear it. Without this, switching off Instagram
  // or deselecting the last Reel after already verifying audio would leave a
  // stale value that still ships in the create payload's official_audio
  // field and still renders in the review-step chip, even though the
  // control that set it is gone.
  useEffect(() => {
    if (platform === 'instagram' && hasReelDeliverable(deliverables)) return;
    if (watch('officialAudioUrl')) setValue('officialAudioUrl', '');
    if (watch('officialAudioTrack')) setValue('officialAudioTrack', null);
  }, [platform, deliverables, watch, setValue]);

  // Per-step mandatory-field checks — mirror campaignSchema's required fields.
  // Now used only to gate the final Launch button; Continue validates via Zod.
  // Steps 5/6 have no inputs of their own, so they're always satisfied.
  const stepComplete = computeStepComplete(watch());
  // Gates the final "Launch Campaign" button — every step with inputs (1-4)
  // must have its mandatory fields filled, not just the currently visible one.
  const canLaunch = stepComplete[1] && stepComplete[2] && stepComplete[3] && stepComplete[4];

  // Continue is always clickable: validate the step, advance if it passes, else
  // toast the first blocking field. Steps 5/6 have no fields, so trigger([]) passes.
  const nextStep = async () => {
    setError(null);
    const fields = stepFields[currentStep] ?? [];
    const isValid = await trigger(fields as (keyof CampaignFormData)[]);
    if (!isValid) {
      toast.error(firstFieldErrorMessage(fields as (keyof CampaignFormData)[], getFieldState));
      return;
    }
    const target = nextStepId(steps, currentStep);
    setCurrentStep(target);
    setMaxReachedStep((m) => Math.max(m, target));
    stepContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const prevStep = () => {
    setError(null);
    setCurrentStep((s) => prevStepId(steps, s));
  };

  // Stepper jumps: to any step already reached this session (back OR forward).
  // Moving beyond maxReachedStep stays gated behind Continue (which validates),
  // so never-visited steps aren't navigable.
  const goToStep = (step: number) => {
    if (step > maxReachedStep) return;
    setError(null);
    setCurrentStep(step);
    stepContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleCategory = (cat: string) => {
    const next = creatorCategories.includes(cat)
      ? creatorCategories.filter((c) => c !== cat)
      : [...creatorCategories, cat];
    setValue('creatorCategories', next, { shouldValidate: true });
  };
  const addCustomCategory = () => {
    const raw = customCategoryInput.trim();
    if (!raw) return;
    if (raw.length > 64) {
      toast.error('Category is too long (max 64 characters).');
      return;
    }
    if (!creatorCategories.some((c) => c.toLowerCase() === raw.toLowerCase()))
      setValue('creatorCategories', [...creatorCategories, raw], { shouldValidate: true });
    setCustomCategoryInput('');
  };
  const toggleTier = (tierValue: string) => {
    const next = tierPresets.includes(tierValue)
      ? tierPresets.filter((v) => v !== tierValue)
      : [...tierPresets, tierValue];
    setValue('tierPresets', next, { shouldValidate: true });
  };
  const setMultiValues = (field: AudienceDropdownKey, values: string[]) => {
    const cleaned = values
      .map((v) => v.trim())
      .filter((v) => v && v.toLowerCase() !== 'any');
    setValue(field, cleaned, { shouldValidate: true });
  };
  const toggleMulti = (field: AudienceDropdownKey, value: string) => {
    const current = ((watch(field) as string[] | undefined) ?? []) as string[];
    const base = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    setMultiValues(field, base);
  };

  const onSubmit = handleSubmit(
    async (data: CampaignFormData) => {
      setIsLoading(true);
      setError(null);
      try {
        // Official Audio Compliance (IG only) now ships as part of the same
        // create body (buildCampaignPayload's official_audio field) — the BE
        // used to reject audio at create time, requiring this separate
        // best-effort follow-up call after the campaign already existed. A
        // failure now fails the whole create, same as every other field,
        // rather than leaving a "created but audio silently didn't save" gap.
        const payload = buildCampaignPayload(data);
        const response = await createCampaign(payload);
        // The campaign exists now: a host follow-up that throws or rejects must not turn this into a failed create.
        const followUp = await Promise.resolve()
          .then(() => afterCreate?.(response, data.platform))
          .catch(() => null);
        if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
        draftDirtyRef.current = false;
        clearNewCampaignDraft(draftKey);
        setSampleVideoFiles([]);
        // Show the Figma "Campaign submitted" success screen (Step 6) instead of
        // redirecting straight to the campaign. The user lands on confirmation
        // and chooses "Go to dashboard" / "Create another".
        setAfterNote(followUp ?? null);
        setSubmitted(response);
        setIsLoading(false);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to create campaign. Please try again.';
        setError(message);
        toast.error(message);
        setIsLoading(false);
      }
    },
    (fieldErrors) => {
      // Validation failed — find the first erroring field, show the message, and go to that step.
      // fieldErrorMessage digs through nested error nodes (e.g. usageRights.days.message) that a
      // flat `.message` read on the object-field error would miss.
      const firstField = Object.keys(fieldErrors)[0] as keyof CampaignFormData | undefined;
      const firstMsg =
        (firstField && fieldErrorMessage(fieldErrors[firstField])) ||
        'Some fields are missing or invalid. Please review each step.';
      const targetStep = firstField ? (FIELD_TO_STEP[firstField] ?? 1) : 1;
      setError(firstMsg);
      toast.error(firstMsg);
      setCurrentStep(targetStep);
    },
  );

  /* ────────────────────── Step 4 — Campaign Brief ── */
  const renderStep4 = () => {
    const CARD = 'rounded-xl border border-border bg-card p-10';
    return (
      <div className="flex flex-col gap-4">
        {/* Header card */}
        <div className={`${CARD} flex items-start gap-3.5`}>
          <span className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-muted">
            <FileText size={20} className="text-foreground" />
          </span>
          <div>
            <h2 className="m-0 text-heading-h5 text-foreground">Campaign brief</h2>
            <p className="m-0 text-body-sm-regular text-muted-foreground">
              What creators need to know.
            </p>
          </div>
        </div>

        {/* Form card */}
        <div className={`${CARD} flex flex-col gap-6`}>
          {/* Title & description */}
          <div className="flex flex-col gap-4">
            <SectionHead icon={TextAlignLeft} title="Title & description" sub="The campaign story creators read first." />
            <div className="flex flex-col gap-1.5">
              <label className={lbl}><span>Brief title</span> <span className={hint}>optional</span></label>
              <Input type="text" placeholder="e.g. Summer Launch Reel" aria-invalid={!!errors.videoTitle} {...register('videoTitle')} className={invalidBorder(!!errors.videoTitle)} />
              {errors.videoTitle && <span className={err}>{errors.videoTitle.message}</span>}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={lbl}><span>Brief description</span> <span className={hint}>optional</span></label>
              <Textarea className={`min-h-[80px] resize-y leading-relaxed ${invalidBorder(!!errors.videoDescription)}`} rows={3} aria-invalid={!!errors.videoDescription} placeholder="Optional long description for the creative brief." {...register('videoDescription')} />
              {errors.videoDescription && <span className={err}>{errors.videoDescription.message}</span>}
            </div>
          </div>

          {/* Products (Multi-Product Campaigns) — collapsible, mirrors the
              "Other input field" expand/collapse below. */}
          <div className="flex flex-col gap-4">
            <button
              type="button"
              onClick={() => setProductsExpanded((v) => !v)}
              className="flex w-full items-center justify-between gap-3 text-left"
              aria-expanded={productsExpanded}
              aria-controls="products-editor-panel"
              aria-label="Products"
            >
              <SectionHead
                icon={ShoppingBag}
                title="Products"
                sub="Add every product in this campaign — our team assigns them to individual creators."
              />
              <CaretDown
                size={18}
                className={`shrink-0 text-muted-foreground transition-transform ${productsExpanded ? 'rotate-180' : ''}`}
              />
            </button>
            {productsExpanded && (
              <div id="products-editor-panel">
                <ProductsEditor
                  items={watch('products') ?? []}
                  onChange={(next) => setValue('products', next, { shouldValidate: false })}
                />
              </div>
            )}
          </div>

          {/* Call to action */}
          <div className="flex flex-col gap-4">
            <SectionHead icon={NotePencil} title="Call to action" sub="What viewers should do next." />
            <div className="flex flex-col gap-1.5">
              <label className={lbl}><span>Call to action</span> <span className={hint}>optional</span></label>
              <Input type="text" placeholder="e.g. Follow @brand and use code SUMMER20" aria-invalid={!!errors.cta} {...register('cta')} className={invalidBorder(!!errors.cta)} />
              {errors.cta && <span className={err}>{errors.cta.message}</span>}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={lbl}><span>Does this campaign need a paid-partnership disclosure (#ad / #sponsored)?</span></label>
              <div className="flex flex-wrap gap-2.5">
                {([['yes', 'Yes'], ['no', 'No']] as const).map(([val, label]) => {
                  const active = (watch('requiresDisclosure') ?? 'yes') === val;
                  return (
                    <button
                      key={val}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setValue('requiresDisclosure', val, { shouldValidate: true })}
                      className={`h-10 cursor-pointer rounded-lg border px-6 text-body-sm-medium transition-colors ${active ? 'border-foreground bg-foreground text-background' : 'border-border bg-card text-foreground hover:border-foreground/30'}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Primary | Secondary focus (Figma: 2-col, surfaced) */}
          <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-1.5">
              <label className={lbl}><span>Primary focus</span> <span className="text-[14px] text-(--foreground-destructive-default)">*</span></label>
              <Textarea className={`min-h-[100px] ${invalidBorder(!!errors.primaryFocus)}`} rows={4} maxLength={2000} aria-invalid={!!errors.primaryFocus} placeholder="e.g. Position Gemini as the AI that gets Indian daily life — a life partner, not a tool. Reinforce Think Bigger every beat." {...register('primaryFocus')} />
              <div className="flex items-center justify-between">
                {errors.primaryFocus && <span className={err}>{errors.primaryFocus.message}</span>}
                <span className={`ml-auto shrink-0 text-caption-regular ${primaryFocus.length > 1900 ? 'text-amber-500' : 'text-muted-foreground'}`}>
                  {primaryFocus.length.toLocaleString()} / 2,000
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={lbl}><span>Secondary focus </span><span className={hint}>optional · up to 5000 chars</span></label>
              <Textarea className={`min-h-[100px] ${invalidBorder(!!errors.secondaryFocus)}`} rows={4} maxLength={5000} aria-invalid={!!errors.secondaryFocus} placeholder="e.g. Showcase multimodal capabilities — voice input, camera (point at fridge → recipe), long-doc summarization. Keep examples culturally native; code-mixed Hindi/English." {...register('secondaryFocus')} />
              <div className="flex items-center justify-between">
                {errors.secondaryFocus && <span className={err}>{errors.secondaryFocus.message}</span>}
                <span className={`ml-auto shrink-0 text-caption-regular ${secondaryFocus.length > 4750 ? 'text-amber-500' : 'text-muted-foreground'}`}>
                  {secondaryFocus.length.toLocaleString()} / 5,000
                </span>
              </div>
            </div>
          </div>

          {/* Brand rules — Do's & don'ts (Figma: 2-col, surfaced) */}
          <div className="flex flex-col gap-4">
            <SectionHead icon={Check} title="Do's & don'ts" sub="Brand rules creators must follow." />
            <div className="grid grid-cols-2 gap-4">
              <BriefList key={`do-${formResetKey}`} kind="do" items={dos} onChange={(v) => setValue('dos', v, { shouldValidate: true })} />
              <BriefList key={`dont-${formResetKey}`} kind="dont" items={donts} onChange={(v) => setValue('donts', v, { shouldValidate: true })} />
            </div>
            {(errors.dos?.message || errors.donts?.message) && (
              <span className={err}>{String(errors.dos?.message ?? errors.donts?.message)}</span>
            )}
          </div>

          {/* Other input field — collapsible (sample / reference URL) */}
          <div className="border-t border-border pt-4">
            <button
              type="button"
              onClick={() => setBriefExpanded((v) => !v)}
              className="flex w-full items-center justify-between"
              aria-expanded={briefExpanded}
              aria-controls="other-input-field-panel"
            >
              <span className="text-body-md-medium text-foreground">Other input field</span>
              <CaretDown size={18} className={`text-muted-foreground transition-transform ${briefExpanded ? 'rotate-180' : ''}`} />
            </button>
            {briefExpanded && (
              <div id="other-input-field-panel" className="mt-4 flex flex-col gap-1.5">
                <label className={lbl}><span>Sample / Reference Video URL</span> <span className={hint}>optional — link creators to a reference style</span></label>
                <Input type="url" placeholder="https://www.youtube.com/watch?v=…" aria-invalid={!!errors.sampleVideoUrl} {...register('sampleVideoUrl')} className={invalidBorder(!!errors.sampleVideoUrl)} />
                {errors.sampleVideoUrl && <span className={err}>{errors.sampleVideoUrl.message}</span>}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ────────────────────── Step 5 — Review & Launch ── */
  const renderStep5 = () => {
    // undefined ("never typed a budget") shows "—", distinct from an explicit
    // 0 which shows "₹0".
    const budgetLabel = totalBudget == null ? '—' : `₹${Number(totalBudget).toLocaleString('en-IN')}`;
    const fmt = (d: string) =>
      d
        ? new Date(d).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
        : '—';
    const startFmt = fmt(startDate);
    const payoutLabel =
      { fixed_fee: 'Fixed Fee', revenue_share: 'Revenue Share', gifting_only: 'Gifting Only' }[
      payoutModel
      ] ?? '—';
    const platformName = platformLabel(platform);
    const tierLabels =
      tierPresets
        .map((v) => TIERS.find((t) => t.value === v)?.label)
        .filter(Boolean)
        .join(', ') || '—';
    const gl = goalLabel(primaryGoalVal, primaryGoalCustomVal);
    const Row = ({ k, v }: { k: string; v: string }) => (
      <div className="flex items-start justify-between gap-2 border-b border-border py-2 last:border-0">
        <span className="shrink-0 text-caption-regular text-muted-foreground">{k}</span>
        <span className="text-right text-caption-medium wrap-break-word text-foreground">{v}</span>
      </div>
    );
    const Card = ({ title, children }: { title: string; children: React.ReactNode }) => (
      <div className="rounded-xl border border-border bg-card p-4">
        <p className="mb-2.5 text-caption-medium tracking-widest text-muted-foreground uppercase">
          {title}
        </p>
        {children}
      </div>
    );
    return (
      <div>
        <div className="flex items-start gap-3.5">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-muted">
            <ClipboardText size={20} className="text-foreground" />
          </span>
          <div>
            <h2 className="m-0 text-heading-h5 text-foreground">Review &amp; launch</h2>
            <p className="m-0 text-body-sm-regular text-muted-foreground">Confirm everything before going live.</p>
          </div>
        </div>
        {/* Dark overview card */}
        <div className="mt-4 mb-5 rounded-xl bg-stone-950 p-6">
          <p className="mb-3 text-caption-medium tracking-[0.12em] uppercase text-white/50">
            Campaign Overview
          </p>
          <h3 className="m-0 mb-4 text-heading-h5 text-white">
            {watch('name') || 'Untitled Campaign'}
          </h3>
          <div className="flex flex-wrap gap-2">
            {[
              primaryGoalVal && gl,
              platform && platformName,
              (region.length ? region.join(', ') : ''),
            ]
              .filter(Boolean)
              .map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-caption-regular text-white/90"
                >
                  <span className="size-1.5 rounded-full bg-white/60" />
                  {tag}
                </span>
              ))}
          </div>
        </div>
        <p className="mb-3 text-caption-medium tracking-[0.08em] text-muted-foreground uppercase">
          Campaign Details
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Card title="Campaign Basics">
            <Row k="Goal" v={gl} />
            <Row k="Platform" v={platformName} />
            {watch('brief') && <Row k="Brief" v={watch('brief') ?? ''} />}
            <Row k="Status" v="Ready" />
          </Card>
          <Card title="Creator Targeting">
            {totalCreators ? <Row k="Creator Count" v={String(totalCreators)} /> : null}
            <Row k="Categories" v={creatorCategories.join(', ') || '—'} />
            <Row k="Creator Tiers" v={tierLabels} />
            <Row k="Creator Gender" v={creatorGenders.length ? creatorGenders.join(', ') : 'All'} />
            <Row k="Regions" v={region.length ? region.join(', ') : 'All'} />
          </Card>
          <Card title="Deliverables">
            {renderDeliverablesSummary(deliverables, isOfferedOnPlatform('usage_rights', platform) ? usageRights : null)}
            {deliverables_text.trim() ? (
              <p className="m-0 mt-2 whitespace-pre-wrap text-caption-regular text-foreground">{deliverables_text}</p>
            ) : null}
            {platform === 'instagram' && hasReelDeliverable(deliverables) ? (
              <ReviewCampaignAudioChip
                url={watch('officialAudioUrl') ?? ''}
                songName={watch('officialAudioTrack')?.song_name}
              />
            ) : null}
          </Card>
          {(Number(ageMin) > 0 || Number(ageMax) > 0 || gender.length > 0 || language.length > 0) && (
            <Card title="Audience Demographics">
              {(Number(ageMin) > 0 || Number(ageMax) > 0) && (
                <Row k="Age Range" v={`${ageMin || '—'} – ${ageMax || '—'}`} />
              )}
              {gender.length > 0 && (
                <Row k="Genders" v={gender.map((g) => g.charAt(0).toUpperCase() + g.slice(1)).join(', ')} />
              )}
              {language.length > 0 && (
                <Row k="Languages" v={language.map((l) => l.charAt(0).toUpperCase() + l.slice(1)).join(', ')} />
              )}
            </Card>
          )}
          <Card title="Budget & Timeline">
            <p className="mb-2 text-heading-h6 text-foreground">{budgetLabel}</p>
            {/* Launch Date row hidden — see Step 3 comment. */}
            <Row k="Payout" v={payoutLabel} />
          </Card>
          {(videoTitle ||
            videoDescription ||
            primaryFocus ||
            secondaryFocus ||
            cta ||
            sampleVideoUrl ||
            dos.length > 0 ||
            donts.length > 0 ||
            sampleVideoFiles[0]) && (
              <div className="col-span-2 rounded-xl border border-border bg-card p-4">
                <p className="mb-2.5 text-caption-medium tracking-widest text-muted-foreground uppercase">
                  Campaign Brief
                </p>
                {videoTitle && <Row k="Title" v={videoTitle} />}
                {videoDescription && <Row k="Video description" v={videoDescription} />}
                {primaryFocus && <Row k="Primary focus" v={primaryFocus} />}
                {secondaryFocus && <Row k="Secondary focus" v={secondaryFocus} />}
                {cta && <Row k="CTA" v={cta} />}
                {dos.length > 0 && <Row k="Do's" v={dos.join(' · ')} />}
                {donts.length > 0 && <Row k="Don'ts" v={donts.join(' · ')} />}
                {sampleVideoFiles[0] && <Row k="Sample video file" v={sampleVideoFiles[0].name} />}
                {sampleVideoUrl && !sampleVideoFiles[0] && (
                  <Row k="Reference URL" v={sampleVideoUrl} />
                )}
              </div>
            )}
        </div>
        {/* suppress unused-variable warning from destructuring */}
        {void startFmt}
      </div>
    );
  };

  /* ────────────────────── Top stepper (Figma) ── */
  const renderStepper = () => (
    <div className="flex items-center rounded-xl border border-border bg-card px-8 py-5">
      {steps.map((step, idx) => {
        const isActive = step.num === currentStep;
        const isCompleted = step.num < currentStep && stepComplete[step.num];
        const filled = isActive || isCompleted;
        const navigable = step.num <= maxReachedStep;
        return (
          <React.Fragment key={step.num}>
            <button
              type="button"
              onClick={() => goToStep(step.num)}
              disabled={!navigable}
              aria-current={isActive ? 'step' : undefined}
              className={`flex shrink-0 items-center gap-2 transition-opacity ${navigable ? 'cursor-pointer hover:opacity-70' : 'cursor-default'}`}
            >
              <div
                className={`flex size-6 items-center justify-center rounded-full text-caption-medium transition-colors ${filled ? 'bg-[#000000] text-background' : 'border border-border bg-card text-muted-foreground'}`}
              >
                {isCompleted ? <Check size={14} strokeWidth={2.5} /> : idx + 1}
              </div>
              <span
                className={`text-body-sm-medium whitespace-nowrap ${filled ? 'text-foreground' : 'text-muted-foreground'}`}
              >
                {step.title}
              </span>
            </button>
            {idx < steps.length - 1 && (
              <div className={`mx-3 h-px flex-1 ${isCompleted ? 'bg-foreground/30' : 'bg-border'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  /* ────────────────────── Step 6 success — "Campaign submitted" (Figma) ── */
  const renderSubmitted = () => {
    const SRow = ({ k, v, dot }: { k: string; v: string; dot?: boolean }) => (
      <div className="flex items-center justify-between gap-4 border-b border-border px-5 py-3.5 last:border-0">
        <span className="text-body-sm-regular text-muted-foreground">{k}</span>
        <span className="flex items-center gap-2 text-right text-body-sm-medium text-foreground">
          {dot && <span className="size-1.5 rounded-full bg-amber-500" />}
          {v}
        </span>
      </div>
    );
    const platformDisplay = (platform || 'instagram').charAt(0).toUpperCase() + (platform || 'instagram').slice(1);
    const tierLabel = tierPresets.length
      ? (TIERS.find((t) => t.value === tierPresets[0])?.label ?? tierPresets[0]) +
        (tierPresets.length > 1 ? ` +${tierPresets.length - 1}` : '')
      : '—';
    const PAYOUT_LABELS: Record<string, string> = { fixed_fee: 'Fixed fee', revenue_share: 'Revenue share', gifting_only: 'Gifting only' };
    const payoutLabel = PAYOUT_LABELS[payoutModel ?? ''] ?? '—';
    // Same nil-vs-zero distinction as renderStep5 — see its comment.
    const budgetLabel = totalBudget == null ? '—' : `₹${Number(totalBudget).toLocaleString('en-IN')}`;
    return (
      <div className="mx-auto flex w-full max-w-[640px] flex-col items-center rounded-2xl border border-border bg-card px-10 py-12 text-center">
        <div className="flex size-28 items-center justify-center rounded-full border border-green-200 bg-green-50">
          <Check size={44} strokeWidth={2} className="text-green-700" />
        </div>
        <h2 className="mt-6 text-heading-h3 text-foreground">{successCopy.title}</h2>
        <p className="mt-2 max-w-[460px] text-body-md-regular text-muted-foreground">{successCopy.message}</p>
        {/* Figma: summary of the submitted campaign (data the brand just entered). */}
        <div className="mt-6 w-full rounded-xl border border-border text-left">
          <SRow k="Campaign" v={submitted?.name || watch('name') || 'Untitled Campaign'} />
          <SRow k="Platform" v={platformDisplay} />
          <SRow k="Creator tier" v={tierLabel} />
          <SRow k="Payout" v={payoutLabel} />
          <SRow k="Budget" v={budgetLabel} />
        </div>
        {afterNote ? (
          <p role="status" className="mt-4 w-full rounded-xl border border-border px-4 py-3 text-left text-body-sm-regular text-foreground">
            {afterNote.note}
          </p>
        ) : null}
        <div className="mt-6 flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              reset({ ...defaultValues });
              if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
              persistTimerRef.current = null;
              draftDirtyRef.current = false;
              persistedStepsRef.current = { currentStep: 1, maxReachedStep: 1 };
              clearNewCampaignDraft(draftKey);
              setSubmitted(null);
              setAfterNote(null);
              setCurrentStep(1);
              setMaxReachedStep(1);
              setFormResetKey((k) => k + 1);
            }}
            className="rounded-lg px-5 py-2 text-body-sm-medium"
          >
            Create another
          </Button>
          {afterNote?.action ? (
            <Button
              type="button"
              variant="outline"
              onClick={afterNote.action.onClick}
              className="rounded-lg px-5 py-2 text-body-sm-medium"
            >
              {afterNote.action.label}
            </Button>
          ) : null}
          <Button
            type="button"
            onClick={() => {
              if (submitted) onCreated(submitted);
            }}
            className="rounded-lg bg-primary px-5 py-2 text-body-sm-semibold text-primary-foreground"
          >
            {successCopy.openLabel}
          </Button>
        </div>
      </div>
    );
  };

  /* ────────────────────── Footer ── */
  const renderFooter = () => {
    const isLast = currentStep === lastStepId(steps);
    return (
      // Figma bottom action bar: full width of <main> (right of sidebar), not viewport-fixed.
      // Rendered outside the scrollable content so it stays pinned at the bottom.
      <div className="flex h-[60px] shrink-0 items-center justify-between border-t border-border bg-card px-6">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="text-body-sm-medium text-foreground-secondary-default transition-colors hover:text-foreground disabled:opacity-50"
        >
          Cancel
        </button>
        <span className="text-caption-regular text-muted-foreground">
          Step {stepPosition(steps, currentStep)} of {steps.length}
        </span>
        <div className="flex items-center gap-3">
          {currentStep > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={prevStep}
              disabled={isLoading}
              className="h-10 rounded-full px-4 py-2 text-body-sm-medium"
            >
              ‹ Back
            </Button>
          )}
          {isLast ? (
            <Button
              type="button"
              onClick={() => {
                if (!canLaunch) {
                  toast.error('Some required fields are missing — please review each step.');
                  return;
                }
                isDraftRef.current = false;
                onSubmit();
              }}
              disabled={isLoading || !canLaunch}
              className="flex h-10 items-center gap-2 rounded-full bg-primary px-5 py-2 text-body-sm-medium text-primary-foreground hover:bg-primary disabled:opacity-50"
            >
              {isLoading ? <LoadingSpinner /> : (<><Rocket size={14} /> Launch Campaign</>)}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={nextStep}
              className="h-10 rounded-full bg-primary px-5 py-2 text-body-sm-medium text-primary-foreground hover:bg-primary"
            >
              Continue ›
            </Button>
          )}
        </div>
      </div>
    );
  };

  /* ────────────────────── Shell ── */
  return (
    // Bound to the viewport below the 52px AppTopBar so the step content scrolls
    // internally and the footer stays pinned at the bottom.
    <div className="animate-in fade-in duration-300 flex h-[calc(100vh-52px)] min-h-0 flex-col overflow-hidden">
      {submitted ? (
        <div className="min-h-0 flex-1 overflow-y-auto py-8">
          <div className="mx-auto w-full max-w-[1000px]">{renderSubmitted()}</div>
        </div>
      ) : (
        <>
          {/* Scrollable step content — fills remaining height, scrolls internally */}
          <div ref={stepContentRef} className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-4 p-8">
          {renderStepper()}
          {/* Global error banner */}
          {error && (
            <Alert variant="destructive" className="shrink-0">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {/* Steps 1 & 2 render their own header + section cards (Figma); the
              remaining steps still use the single content card until migrated. */}
          {currentStep === 1 && (
            <StepBasics
              platform={platform}
              primaryGoalVal={primaryGoalVal}
              name={name}
              brief={brief}
              register={register}
              setValue={setValue}
              errors={errors}
            />
          )}
          {currentStep === 2 && (
            <StepTargeting
              register={register}
              setValue={setValue}
              errors={errors}
              totalCreators={totalCreators}
              ageMin={ageMin}
              ageMax={ageMax}
              gender={gender}
              language={language}
              region={region}
              creatorGenders={creatorGenders}
              creatorCategories={creatorCategories}
              tierPresets={tierPresets}
              toggleCategory={toggleCategory}
              addCustomCategory={addCustomCategory}
              toggleTier={toggleTier}
              toggleMulti={toggleMulti}
              setMultiValues={setMultiValues}
              customCategoryInput={customCategoryInput}
              setCustomCategoryInput={setCustomCategoryInput}
            />
          )}
          {currentStep === 3 && (
            <StepBudget
              register={register}
              setValue={setValue}
              control={control}
              errors={errors}
              platform={platform}
              deliverables={deliverables}
              deliverables_text={deliverables_text}
              usageRights={usageRights}
              totalBudget={totalBudget}
              payoutModel={payoutModel}
            />
          )}
          {currentStep === 4 && renderStep4()}
          {currentStep === CONNECT_STEP && connectStep}
          {currentStep === 6 && (
            <div className="flex flex-col rounded-2xl border border-border bg-card">
            <div className="p-6">
            {renderStep5()}
            </div>
            </div>
          )}
          </div>
          </div>{/* end flex-1 overflow-y-auto */}
          {renderFooter()}
        </>
      )}

      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        .ncw-range::-webkit-slider-thumb { -webkit-appearance:none; appearance:none; width:18px; height:18px; border-radius:9999px; background:#fff; border:2px solid #171717; pointer-events:auto; cursor:pointer; box-shadow:0 1px 2px rgba(0,0,0,.15); }
        .ncw-range::-moz-range-thumb { width:18px; height:18px; border-radius:9999px; background:#fff; border:2px solid #171717; pointer-events:auto; cursor:pointer; }
        .ncw-range::-webkit-slider-runnable-track { background:transparent; } .ncw-range::-moz-range-track { background:transparent; }`}</style>
    </div>
  );
}
